import React, { useEffect } from "react";
import { createPortal } from "react-dom";

export default function useZoomModal({
  zoomOpen,
  setZoomOpen,
  zoomSrc,
  zoomType,
}) {
  // Lock background scroll while the zoom overlay is open (all platforms).
  useEffect(() => {
    if (!zoomOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [zoomOpen]);

   // Temporarily re-enable pinch-zoom while this modal is open. The app
  // locks zoom globally via the viewport meta tag (index.html), but a
  // fullscreen image/video preview is exactly where pinch-to-zoom is
  // expected — so we relax the restriction here and restore the app-wide
  // lock the moment the modal closes.
  useEffect(() => {
    if (!zoomOpen) return;
    const meta = document.querySelector('meta[name="viewport"]');
    const prevContent = meta?.getAttribute("content");
    meta?.setAttribute(
      "content",
      "width=device-width, initial-scale=1, maximum-scale=5, user-scalable=yes, viewport-fit=cover, interactive-widget=resizes-content"
    );

    // touch-action is computed as the INTERSECTION of an element's own
    // value and every ancestor's value. Since html/body/#root are locked
    // to `pan-y` app-wide, setting touch-action on the modal's own div
    // alone can't restore pinch — the ancestor chain still vetoes it. We
    // have to relax it on the ancestors too while the modal is open.
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlTouchAction = html.style.touchAction;
    const prevBodyTouchAction = body.style.touchAction;
    html.style.touchAction = "manipulation";
    body.style.touchAction = "manipulation";

    return () => {
      if (meta && prevContent) meta.setAttribute("content", prevContent);
      html.style.touchAction = prevHtmlTouchAction;
      body.style.touchAction = prevBodyTouchAction;
    };
  }, [zoomOpen]);

  if (!zoomOpen) return null;

  const modal = (
    <div
      onClick={() => setZoomOpen(false)}
      className="fixed inset-0 z-[999] bg-black flex items-center justify-center"
      style={{ height: "100dvh", width: "100dvw", touchAction: "manipulation" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full h-full flex items-center justify-center p-4"
      >
        {/* CLOSE BUTTON */}
        <button
          onClick={() => setZoomOpen(false)}
          className="fixed top-4 right-4 z-[1000] w-9 h-9 rounded-full bg-black/70 text-white flex items-center justify-center text-xl"
          style={{ top: "calc(env(safe-area-inset-top, 0px) + 16px)" }}
          aria-label="Close"
        >
          ×
        </button>

        {/* IMAGE — object-contain + bounded box shows the WHOLE file,
            never crops it, and scales down to fit any screen. */}
        {zoomSrc && zoomType === "image" && (
          <img
            src={zoomSrc}
            alt="Post preview"
            className="max-w-full max-h-full w-auto h-auto object-contain select-none"
            style={{ maxHeight: "calc(100dvh - 2rem)", maxWidth: "calc(100dvw - 2rem)" }}
          />
        )}

        {/* VIDEO */}
        {zoomSrc && zoomType === "video" && (
          <video
            src={zoomSrc}
            controls
            autoPlay
            className="max-w-full max-h-full w-auto h-auto rounded-lg object-contain"
            style={{ maxHeight: "calc(100dvh - 2rem)", maxWidth: "calc(100dvw - 2rem)" }}
          />
        )}
      </div>
    </div>
  );

  // Always portal to <body>. useZoomModal is invoked inline from deep
  // inside component trees (e.g. Feed.js), whose ancestors can be
  // `position: relative` + `overflow-y-auto` containers. A non-portaled
  // "fixed" element still gets clipped to such an ancestor's box on some
  // browsers, and any future `md:` variant reintroducing `absolute` would
  // silently break full-screen coverage again. Portaling removes that
  // fragility entirely so the overlay is guaranteed to cover the true
  // viewport on Android, iPhone, and desktop alike.
  return createPortal(modal, document.body);
}